import * as React from "react";
import { Container, Loader, Header, Grid, Divider } from "semantic-ui-react";
import { mean } from "lodash";
import { connect } from "react-redux";
import { isRequestActive } from "redux-request-loading";

import { VisualSummary } from "@teamprevent/common/stressRiskAssessment/components/VisualSummary";
import { ManagerSraQuestionnaireResultModel } from "@teamprevent/common/stressRiskAssessment/models";

import { PrintDialog } from "@teamprevent/common/components/PrintDialog";

import { loadSraResults, loadSraResultsForCase } from "../actions";
import { LOAD_SRA_RESULTS } from "../actionTypes";
import { ApplicationState } from "../../applicationState";
import "../styles/sra-summary.scss";

interface StateProps {
    caseId?: string;
    sraEmployeeSectionId?: string;
    sraResults: ManagerSraQuestionnaireResultModel;
    loading: boolean;
}

interface DispatchProps {
    loadSraResults: (sraEmployeeSectionId: string) => void;
    loadSraResultsForCase: (caseId: string) => void;
}

type Props = StateProps & DispatchProps;

export const ManagerSraSummaryUnconnected: React.FunctionComponent<Props> = (props: Props) => {
    React.useEffect(() => {
        if (props.sraEmployeeSectionId) {
            props.loadSraResults(props.sraEmployeeSectionId);
        }
        else {
            props.loadSraResultsForCase(props.caseId);
        }
    }, []);

    if (props.loading || props.sraResults === null) {
        return <Loader active>Loading</Loader>;
    }

    return (
        <Container className="sra-summary-container">
            <Grid container>
                <Grid.Row className="report-title-row">
                    <Grid.Column>
                        <Header as="h1">Stress Risk Assessment Summary</Header>
                    </Grid.Column>
                </Grid.Row>
            </Grid>

            <Grid container className="details-grid">
                <Grid.Row className="report-section-row">
                    <Grid.Column>
                        <Header as="h4">Details</Header>
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column className="primary-column" computer={6} mobile={16}>
                        Name
                    </Grid.Column>
                    <Grid.Column computer={10} mobile={16}>
                        {props.sraResults?.firstName} {props.sraResults?.lastName}
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column className="primary-column" computer={6} mobile={16}>
                        Date completed
                    </Grid.Column>
                    <Grid.Column computer={10} mobile={16}>
                        {props.sraResults?.dateCompleted && props.sraResults?.dateCompleted?.format("DD.MM.YYYY")}
                    </Grid.Column>
                </Grid.Row>
            </Grid>

            <Grid container>
                <Grid.Row>

                    {
                        props.sraResults?.questionnaireScores !== null ?
                            <>
                                <p className="summary-paragraph">
                                    {/* eslint-disable-next-line max-len */}
                                Your employee has completed their Stress Risk Survey. Their answers have been analysed and the results are shown below, together with some guidance notes on how you may be able to support your employee in those areas where they have indicated as potential stressors.
                                </p>
                                <p className="summary-paragraph">
                                    {/* eslint-disable-next-line max-len */}
                                Please now arrange to meet with your employee to discuss the outcome of their Stress Risk Survey and agree an action plan. A suggested action plan template can be found here:
                                </p>
                                <p className="summary-paragraph">
                                    <a
                                        href="http://www.hse.gov.uk/stress/assets/docs/actionplan.pdf"
                                        target="about:blank"
                                        rel="noopener noreferrer"
                                    >
                                        http://www.hse.gov.uk/stress/assets/docs/actionplan.pdf
                                    </a>
                                </p>
                                <VisualSummary questionnaireResult={props.sraResults as ManagerSraQuestionnaireResultModel} />
                            </>
                            :
                            <>
                                <p className="summary-paragraph">
                                    {/* eslint-disable-next-line max-len */}
                                Your employee has not completed their Stress Risk Survey. Their analysed answers will be available here once the survey is completed.
                                </p>
                            </>
                    }
                </Grid.Row>
            </Grid>

            <Divider />

            <PrintDialog />
        </Container>
    );
};

const mapStateToProps = (state: ApplicationState): StateProps => {

    const sraResults = state.sraResults;

    if (sraResults !== null && sraResults.questionnaireScores !== null) {
        sraResults.overallScore = mean(sraResults.questionnaireScores.map(q => q.meanScore));
    }
    return {
        caseId: state.router.params.caseId,
        sraEmployeeSectionId: state.router.params.sraEmployeeSectionId,
        sraResults,
        loading: isRequestActive(state, LOAD_SRA_RESULTS)
    };
};

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
    loadSraResultsForCase: (caseId: string) => dispatch(loadSraResultsForCase(caseId)),
    loadSraResults: (sraEmployeeSectionId: string) => dispatch(loadSraResults(sraEmployeeSectionId)),
});

export const ManagerSraSummary =
    connect(mapStateToProps, mapDispatchToProps)(ManagerSraSummaryUnconnected);
